var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataProvider, useGetOne, useNotify, useRefresh } from "react-admin";
import { useMutation, useQuery } from "@apollo/client";
import { useQuery as useLookup } from "@tanstack/react-query";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, Radio, TextField, Tooltip, } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Dialog } from "@sequentech/ui-essentials";
import { useSignedAction } from "@/hooks/useSignedAction";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { CHANGE_APPLICATION_STATUS } from "@/queries/ChangeApplicationStatus";
import { USER_PROFILE_ATTRIBUTES } from "@/queries/GetUserProfileAttributes";
import { getAttributeLabel } from "@/services/UserService";
import { ApplicationsError, IApplicationsStatus, RejectReason } from "@/types/applications";
import { ApprovalStatusChip } from "./ApprovalChips";
import { ApprovalVoterStep, NO_VOTER } from "./ApprovalVoterStep";
import { convertToCamelCase } from "./UtilsApprovals";
import { EIdentityMethod, conditionLabels, profileFieldLabel, rejectionReasonKey, } from "./approvalMatrix";
import { applicantData, applicantName, candidateFilters, decidedBy, decisionDetails, differenceText, differingFields, displayValue, initials, joinList, listAnnotation, rankCandidates, searchFilters, voterName, waitingTime, } from "./approvalReview";
import { Avatar, Muted, Notice, NoticeTitle, NumberBadge, OptionCard, OptionTitle, Overline, Tag, TagRow, } from "./approvalStyles";
var JOINT_NAME_DOCUMENTS = ["seamanBook", "driversLicense"];
var ID_CARD_TYPE_FIELD = "sequent.read-only.id-card-type";
/** How many registry voters each lookup asks for. */
var LOOKUP_SIZE = 10;
/** How many registry voters the step offers. */
var CANDIDATES_SHOWN = 5;
var STEPS = ["identity", "voter", "decide"];
var Stepper = styled("ol")({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
    margin: "24px 0 16px",
    padding: 0,
    listStyle: "none",
});
var StepItem = styled("li")({
    "display": "flex",
    "alignItems": "center",
    "gap": "8px",
    "fontSize": "15px",
    "color": "#5B6272",
    "&[aria-current='step'], &[data-done='true']": { color: "#191D23" },
    "&:not(:last-of-type)::after": {
        content: '""',
        width: "min(160px, 10vw)",
        borderTop: "1px dashed #8A90A0",
        margin: "0 4px",
    },
});
var Section = styled(Accordion)({
    "width": "100%",
    "margin": "0 0 8px !important",
    "boxShadow": "0 1px 2px rgba(15, 5, 76, 0.12)",
    "&::before": { display: "none" },
});
var DetailsTable = styled("table")({
    "width": "100%",
    "borderCollapse": "collapse",
    "border": "1px solid #E3E7EF",
    "fontSize": "14px",
    "& th, & td": {
        padding: "8px 16px",
        borderBottom: "1px solid #E3E7EF",
        textAlign: "left",
    },
    "& th": { width: "40%", fontWeight: 600 },
});
var Footer = styled("div")(function (_a) {
    var theme = _a.theme;
    return ({
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        gap: "12px",
        marginTop: "32px",
        padding: "16px",
        background: theme.palette.white,
        boxShadow: "0 -2px 6px rgba(15, 5, 76, 0.08)",
    });
});
export var ViewApproval = function (_a) {
    var _b, _c, _d;
    var electionEventId = _a.electionEventId, electionId = _a.electionId, currApprovalId = _a.currApprovalId, goBack = _a.goBack, onViewRule = _a.onViewRule;
    var _e = useTranslation(), t = _e.t, i18n = _e.i18n;
    var tenantId = useTenantStore()[0];
    var notify = useNotify();
    var refresh = useRefresh();
    var dataProvider = useDataProvider();
    var openSigning = useSignedAction();
    var _f = useState("identity"), step = _f[0], setStep = _f[1];
    var _g = useState(false), faceToFace = _g[0], setFaceToFace = _g[1];
    var _h = useState(null), chosen = _h[0], setChosen = _h[1];
    var _j = useState(""), search = _j[0], setSearch = _j[1];
    var _k = useState(""), lookedUp = _k[0], setLookedUp = _k[1];
    var _l = useState(null), verdict = _l[0], setVerdict = _l[1];
    var _m = useState(RejectReason.NO_VOTER), reason = _m[0], setReason = _m[1];
    var _o = useState(""), message = _o[0], setMessage = _o[1];
    var _p = useState(false), confirmApprove = _p[0], setConfirmApprove = _p[1];
    var _q = useState(false), sending = _q[0], setSending = _q[1];
    var _r = useState(false), copied = _r[0], setCopied = _r[1];
    var now = useMemo(function () { return new Date(); }, []);
    var userAttributes = useQuery(USER_PROFILE_ATTRIBUTES, { variables: { tenantId: tenantId, electionEventId: electionEventId } }).data;
    var _s = useGetOne("sequent_backend_applications", { id: currApprovalId }), task = _s.data, isLoading = _s.isLoading, error = _s.error;
    var changeStatus = useMutation(CHANGE_APPLICATION_STATUS)[0];
    // The registry is searched a moment after the administrator stops typing.
    useEffect(function () {
        var timer = setTimeout(function () { return setLookedUp(search.trim()); }, 400);
        return function () { return clearTimeout(timer); };
    }, [search]);
    var profileAttributes = userAttributes === null || userAttributes === void 0 ? void 0 : userAttributes.get_user_profile_attributes;
    var fieldLabel = useMemo(function () { return profileFieldLabel(profileAttributes !== null && profileAttributes !== void 0 ? profileAttributes : [], t, getAttributeLabel); }, [profileAttributes, t]);
    var decision = useMemo(function () { return (task ? decisionDetails(task) : null); }, [task]);
    var comparedFields = useMemo(function () {
        var _a;
        var decided = Object.keys((_a = decision === null || decision === void 0 ? void 0 : decision.fields) !== null && _a !== void 0 ? _a : {});
        var asked = task ? listAnnotation(task, "search-attributes") : [];
        // The decision lists the fields in alphabetical order; keep the order asked for.
        return decided.length > 0
            ? __spreadArray(__spreadArray([], asked.filter(function (field) { return decided.includes(field); }), true), decided.filter(function (field) { return !asked.includes(field); }), true) : asked;
    }, [decision, task]);
    var unsetAttributes = useMemo(function () { return (task ? listAnnotation(task, "unset-attributes") : []); }, [task]);
    var status = String((_b = task === null || task === void 0 ? void 0 : task.status) !== null && _b !== void 0 ? _b : "").toUpperCase();
    var isAccepted = status === IApplicationsStatus.ACCEPTED;
    var filters = useMemo(function () {
        return !task
            ? []
            : lookedUp
                ? searchFilters(lookedUp)
                : candidateFilters(task, comparedFields);
    }, [task, lookedUp, comparedFields]);
    var lookup = useLookup({
        queryKey: ["approval-candidates", electionEventId, electionId, currApprovalId, filters],
        enabled: !!task && !isAccepted && filters.length > 0,
        queryFn: function () { return __awaiter(void 0, void 0, void 0, function () {
            var pages;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, Promise.all(filters.map(function (filter) {
                            return dataProvider.getList("user", {
                                filter: __assign({ tenant_id: tenantId, election_event_id: electionEventId, election_id: electionId }, filter),
                                pagination: { page: 1, perPage: LOOKUP_SIZE },
                                sort: { field: "id", order: "ASC" },
                            });
                        }))];
                    case 1:
                        pages = _a.sent();
                        return [2 /*return*/, pages.flatMap(function (page) { return page.data; })];
                }
            });
        }); },
    });
    var candidates = useMemo(function () {
        var _a;
        return task
            ? rankCandidates(task, (_a = lookup.data) !== null && _a !== void 0 ? _a : [], comparedFields, unsetAttributes).slice(0, CANDIDATES_SHOWN)
            : [];
    }, [task, lookup.data, comparedFields, unsetAttributes]);
    if (isLoading) {
        return _jsx(CircularProgress, { "aria-label": String(t("approvalsScreen.list.title")) });
    }
    var backButton = (_jsx(Button, { variant: "secondary", onClick: goBack, startIcon: _jsx(ArrowBackIosNewIcon, {}), children: t("approvalsScreen.flow.backToList") }));
    if (error || !task) {
        return (_jsxs(Box, { sx: { padding: "24px 16px" }, children: [_jsx(Notice, { "data-tone": "error", role: "alert", sx: { marginBottom: "16px" }, children: t("approvalsScreen.review.loadError") }), backButton] }));
    }
    var data = applicantData(task);
    var name = applicantName(task) || String(t("approvalsScreen.list.unnamed"));
    var isPending = status === IApplicationsStatus.PENDING;
    var canDecide = !isAccepted;
    var identity = (_c = decision === null || decision === void 0 ? void 0 : decision.identity) !== null && _c !== void 0 ? _c : null;
    var typedByHand = identity === EIdentityMethod.MANUAL_ENTRY;
    var jointNames = JOINT_NAME_DOCUMENTS.includes((_d = data[ID_CARD_TYPE_FIELD]) !== null && _d !== void 0 ? _d : "");
    var chosenCandidate = candidates.find(function (candidate) { return candidate.voter.id === chosen; });
    var best = candidates.find(function (candidate) { return !candidate.alreadyEnrolled; });
    var officer = decidedBy(task);
    var formatDate = function (value, withTime) {
        if (withTime === void 0) { withTime = true; }
        return value
            ? new Date(value).toLocaleString(i18n.language, __assign({ dateStyle: "medium" }, (withTime ? { timeStyle: "short" } : {})))
            : "-";
    };
    var whyApproveIsOff = !chosenCandidate
        ? chosen === NO_VOTER
            ? t("approvalsScreen.flow.decide.noVoter")
            : t("approvalsScreen.flow.decide.chooseVoter")
        : chosenCandidate.alreadyEnrolled
            ? t("approvalsScreen.flow.decide.enrolled")
            : typedByHand && !faceToFace
                ? t("approvalsScreen.flow.decide.faceToFace")
                : null;
    var canApprove = whyApproveIsOff === null;
    // Approving is offered first; rejecting only once no voter was found.
    var effectiveVerdict = verdict !== null && verdict !== void 0 ? verdict : (canApprove ? "approve" : isPending && chosen === NO_VOTER ? "reject" : null);
    var messageMissing = reason === RejectReason.OTHER && !message.trim();
    var why = function () {
        var _a, _b;
        var rejectionReason = String(t(rejectionReasonKey(((_a = task.annotations) !== null && _a !== void 0 ? _a : {}).rejection_reason))).toLowerCase();
        if (isAccepted) {
            return officer
                ? t("approvalsScreen.review.why.approvedBy", {
                    name: officer,
                    date: formatDate(task.updated_at, false),
                })
                : t("approvalsScreen.review.why.approvedAuto");
        }
        if (status === IApplicationsStatus.REJECTED) {
            return officer
                ? t("approvalsScreen.review.why.rejectedBy", {
                    name: officer,
                    date: formatDate(task.updated_at, false),
                    reason: rejectionReason,
                })
                : t("approvalsScreen.review.why.rejectedAuto", { reason: rejectionReason });
        }
        if (!decision) {
            return t("approvalsScreen.review.why.unknown");
        }
        if (typedByHand) {
            return t("approvalsScreen.review.why.typedByHand");
        }
        if (decision.approvedVoters > 1) {
            return t("approvalsScreen.review.why.severalVoters");
        }
        if (!decision.voterFound) {
            return t("approvalsScreen.review.why.noVoter");
        }
        var differing = differingFields(decision);
        if (differing.length === 0) {
            return t("approvalsScreen.review.why.pending");
        }
        // The registry values are those of the closest voter, when it differs in the same details.
        var differences = ((_b = best === null || best === void 0 ? void 0 : best.details) !== null && _b !== void 0 ? _b : []).filter(function (detail) { return !detail.same; });
        var sameDetails = differences.length > 0 &&
            differences
                .flatMap(function (detail) { return detail.fields; })
                .every(function (field) { return differing.includes(field); });
        return sameDetails
            ? t("approvalsScreen.review.why.differs", {
                count: differences.length,
                details: differences
                    .map(function (detail) { return differenceText(detail, t, fieldLabel); })
                    .join("; "),
            })
            : t("approvalsScreen.review.why.differsFields", {
                count: differing.length,
                fields: joinList(differing.map(function (field) { return fieldLabel(field).toLowerCase(); }), t),
            });
    };
    var approve = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a, reply, errors, _b;
        var _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    setConfirmApprove(false);
                    if (!chosenCandidate) {
                        return [2 /*return*/];
                    }
                    setSending(true);
                    _e.label = 1;
                case 1:
                    _e.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, changeStatus({
                            variables: {
                                tenant_id: tenantId,
                                id: task.id,
                                user_id: chosenCandidate.voter.id,
                                area_id: task.area_id,
                                election_event_id: electionEventId,
                            },
                        })];
                case 2:
                    _a = _e.sent(), reply = _a.data, errors = _a.errors;
                    if (((_c = reply === null || reply === void 0 ? void 0 : reply.ApplicationChangeStatus) === null || _c === void 0 ? void 0 : _c.error) || errors) {
                        notify(((_d = reply === null || reply === void 0 ? void 0 : reply.ApplicationChangeStatus) === null || _d === void 0 ? void 0 : _d.error) === ApplicationsError.APPROVED_VOTER
                            ? t("approvalsScreen.notifications.VoterApprovedAlready")
                            : t("approvalsScreen.notifications.approveError"), { type: "error" });
                        return [2 /*return*/];
                    }
                    // Approving a voter may wait for signatures: its panel opens instead.
                    if (!openSigning(reply === null || reply === void 0 ? void 0 : reply.ApplicationChangeStatus, { onChange: function () { return refresh(); } })) {
                        notify(t("approvalsScreen.notifications.approveSuccess", { name: name }), {
                            type: "success",
                        });
                    }
                    goBack();
                    return [3 /*break*/, 5];
                case 3:
                    _b = _e.sent();
                    notify(t("approvalsScreen.notifications.approveError"), { type: "error" });
                    return [3 /*break*/, 5];
                case 4:
                    setSending(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var reject = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setSending(true);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, changeStatus({
                            variables: {
                                tenant_id: tenantId,
                                id: task.id,
                                user_id: "",
                                area_id: task.area_id,
                                election_event_id: electionEventId,
                                rejection_reason: reason,
                                rejection_message: reason === RejectReason.OTHER ? message.trim() : undefined,
                            },
                        })];
                case 2:
                    _b.sent();
                    notify(t("approvalsScreen.notifications.rejectSuccess", { name: name }), { type: "success" });
                    goBack();
                    return [3 /*break*/, 5];
                case 3:
                    _a = _b.sent();
                    notify(t("approvalsScreen.notifications.rejectError"), { type: "error" });
                    return [3 /*break*/, 5];
                case 4:
                    setSending(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var copyId = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, navigator.clipboard.writeText(String(task.id))];
                case 1:
                    _b.sent();
                    setCopied(true);
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    setCopied(false);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var done = function (candidate) {
        return step !== null && STEPS.indexOf(candidate) < STEPS.indexOf(step);
    };
    var section = function (key, summary, children) {
        var number = STEPS.indexOf(key) + 1;
        return (_jsxs(Section, { expanded: step === key, onChange: function (_, expanded) { return setStep(expanded ? key : null); }, disableGutters: true, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), id: "approval-step-".concat(key), "aria-controls": "approval-step-".concat(key, "-content"), children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: "12px", flexGrow: 1 }, children: [_jsx(NumberBadge, { "data-pending": step !== key && !done(key), children: done(key) ? _jsx(CheckIcon, { fontSize: "small" }) : number }), _jsx(Box, { component: "h3", sx: { margin: 0, fontSize: "20px", fontWeight: 600 }, children: t("approvalsScreen.flow.steps.".concat(key)) })] }), step !== key && summary && (_jsx(Box, { sx: { alignSelf: "center", marginRight: "12px" }, children: _jsx(Muted, { children: summary }) }))] }), _jsx(AccordionDetails, { sx: { padding: "0 20px 24px" }, children: children })] }, key));
    };
    var identityMethod = identity !== null && identity !== void 0 ? identity : "UNKNOWN";
    var identitySummary = typedByHand
        ? faceToFace
            ? t("approvalsScreen.flow.identity.checked")
            : t("approvalsScreen.flow.identity.notChecked")
        : t("approvalsScreen.idCheck.method.".concat(identityMethod));
    var voterSummary = chosen === NO_VOTER
        ? t("approvalsScreen.flow.voter.noneChosen")
        : chosenCandidate
            ? "".concat(voterName(chosenCandidate.voter), " \u00B7 ").concat(t("approvalsScreen.review.detailsMatch", {
                count: chosenCandidate.matching,
                total: chosenCandidate.details.length,
            }))
            : t("approvalsScreen.flow.voter.notChosen");
    var detailRows = (profileAttributes !== null && profileAttributes !== void 0 ? profileAttributes : []).flatMap(function (attribute) {
        var _a, _b;
        var key = convertToCamelCase((_a = attribute.name) !== null && _a !== void 0 ? _a : "");
        var value = data[key];
        return attribute.name && value
            ? [
                _jsxs("tr", { children: [_jsx("th", { scope: "row", children: t(getAttributeLabel((_b = attribute.display_name) !== null && _b !== void 0 ? _b : attribute.name)) }), _jsx("td", { children: t(value, { defaultValue: displayValue(value, i18n.language) }) })] }, attribute.name),
            ]
            : [];
    });
    var identityStep = (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }, children: [_jsx(Box, { component: "h4", sx: { margin: 0, fontSize: "15px" }, children: t("approvalsScreen.idCheck.title") }), _jsx(Tag, { sx: identity === EIdentityMethod.VERIFIED
                            ? { background: "#E7F8EF", borderColor: "#A9E0C3", color: "#0B6B43" }
                            : typedByHand
                                ? { background: "#FFF3CD", borderColor: "#F0D58C", color: "#7A5200" }
                                : undefined, children: t("approvalsScreen.idCheck.method.".concat(identityMethod)) })] }), _jsx(Box, { sx: { marginBottom: "16px" }, children: identity === EIdentityMethod.VERIFIED
                    ? t("approvalsScreen.idCheck.verified")
                    : typedByHand
                        ? t("approvalsScreen.idCheck.typedByHand")
                        : t("approvalsScreen.idCheck.unknown") }), typedByHand && canDecide && (_jsxs(Notice, { "data-tone": "warning", sx: { marginBottom: "16px" }, children: [_jsx(WarningAmberIcon, { "aria-hidden": true }), _jsxs("div", { children: [_jsx(NoticeTitle, { children: t("approvalsScreen.idCheck.faceToFaceTitle") }), t("approvalsScreen.idCheck.faceToFaceText"), _jsx(FormControlLabel, { sx: { display: "flex", marginTop: "8px" }, control: _jsx(Checkbox, { checked: faceToFace, onChange: function (event) { return setFaceToFace(event.target.checked); } }), label: String(t("approvalsScreen.flow.identity.confirm")) })] })] })), _jsx(Box, { component: "h4", sx: { margin: "24px 0 12px", fontSize: "15px" }, children: t("approvalsScreen.flow.identity.details") }), _jsx(DetailsTable, { children: _jsxs("tbody", { children: [detailRows, _jsxs("tr", { children: [_jsx("th", { scope: "row", children: t("approvalsScreen.review.applicationId") }), _jsxs("td", { children: [_jsx(Box, { component: "code", sx: { fontSize: "13px" }, children: String(task.id) }), _jsx(Tooltip, { title: String(t(copied
                                                ? "approvalsScreen.review.copied"
                                                : "approvalsScreen.review.copy")), children: _jsx(IconButton, { size: "small", "aria-label": String(t("approvalsScreen.review.copy")), onClick: copyId, children: _jsx(ContentCopyIcon, { sx: { fontSize: "16px" } }) }) })] })] })] }) })] }));
    var decideStep = (_jsxs(Box, { sx: { maxWidth: "720px" }, children: [_jsxs(Box, { role: "radiogroup", "aria-label": String(t("approvalsScreen.flow.steps.decide")), sx: { display: "flex", flexDirection: "column", gap: "12px" }, children: [_jsxs(OptionCard, { "data-selected": effectiveVerdict === "approve", "data-disabled": !canApprove, children: [_jsx(Radio, { sx: { padding: "2px" }, name: "approval-verdict", checked: effectiveVerdict === "approve", disabled: !canApprove, onChange: function () { return setVerdict("approve"); } }), _jsx(CheckIcon, { sx: { color: "#0B6B43" }, "aria-hidden": true }), _jsxs("div", { children: [_jsx(OptionTitle, { children: t("approvalsScreen.flow.decide.approve") }), _jsx(Muted, { children: whyApproveIsOff !== null && whyApproveIsOff !== void 0 ? whyApproveIsOff : t("approvalsScreen.flow.decide.approveText", {
                                            voter: chosenCandidate ? voterName(chosenCandidate.voter) : "",
                                        }) })] })] }), isPending && (_jsxs(OptionCard, { "data-selected": effectiveVerdict === "reject", children: [_jsx(Radio, { sx: { padding: "2px" }, name: "approval-verdict", checked: effectiveVerdict === "reject", onChange: function () { return setVerdict("reject"); } }), _jsx(CloseIcon, { sx: { color: "#DC2626" }, "aria-hidden": true }), _jsxs("div", { children: [_jsx(OptionTitle, { children: t("approvalsScreen.flow.decide.reject") }), _jsx(Muted, { children: t("approvalsScreen.flow.decide.rejectText") })] })] }))] }), isPending && effectiveVerdict === "reject" && (_jsxs(Box, { sx: { margin: "24px 0 0 48px" }, children: [_jsx(Box, { component: "h4", sx: { margin: "0 0 8px", fontSize: "15px" }, children: t("approvalsScreen.reject.rejectReason") }), _jsx(Box, { role: "radiogroup", "aria-label": String(t("approvalsScreen.reject.rejectReason")), children: Object.values(RejectReason).map(function (value) { return (_jsxs(Box, { component: "label", sx: { display: "flex", gap: "8px", marginBottom: "8px" }, children: [_jsx(Radio, { sx: { padding: "2px", alignSelf: "flex-start" }, name: "approval-reject-reason", checked: reason === value, onChange: function () { return setReason(value); } }), _jsxs("div", { children: [_jsx(OptionTitle, { sx: { fontSize: "14px" }, children: t("approvalsScreen.reject.reasons.".concat(value)) }), _jsx(Muted, { children: t("approvalsScreen.reject.hint.".concat(value)) })] })] }, value)); }) }), reason === RejectReason.OTHER ? (_jsx(TextField, { fullWidth: true, multiline: true, minRows: 2, sx: { marginTop: "8px" }, label: String(t("approvalsScreen.reject.message")), value: message, onChange: function (event) { return setMessage(event.target.value); }, error: messageMissing, helperText: messageMissing
                            ? String(t("approvalsScreen.reject.messageRequired"))
                            : undefined })) : (_jsx(Notice, { "data-tone": "neutral", sx: { marginTop: "8px" }, children: _jsxs("div", { children: [_jsx(Overline, { sx: { margin: "0 0 4px" }, children: t("approvalsScreen.reject.previewTitle") }), t("approvalsScreen.reject.preview.".concat(reason))] }) }))] }))] }));
    var next = function () {
        var index = step ? STEPS.indexOf(step) : -1;
        setStep(STEPS[Math.min(index + 1, STEPS.length - 1)]);
    };
    return (_jsxs(Box, { sx: { padding: "24px 16px 0" }, children: [_jsxs(Box, { sx: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "16px" }, children: [_jsx(Avatar, { "data-size": "large", "aria-hidden": true, children: initials(name) }), _jsxs(Box, { sx: { flexGrow: 1, minWidth: 0 }, children: [_jsx(Box, { component: "h2", sx: { margin: 0, fontSize: "24px", fontWeight: 600 }, children: name }), _jsx(Muted, { children: [
                                    data.email,
                                    t("approvalsScreen.review.applied", {
                                        date: formatDate(task.created_at),
                                    }),
                                    isPending
                                        ? t("approvalsScreen.review.waiting", {
                                            time: waitingTime(task.created_at, now, t),
                                        })
                                        : "",
                                ]
                                    .filter(function (part) { return part; })
                                    .join(" · ") })] }), _jsx(ApprovalStatusChip, { status: status })] }), _jsxs(Notice, { "data-tone": isPending ? "warning" : isAccepted ? "success" : "neutral", sx: { marginTop: "16px" }, children: [isPending ? _jsx(WarningAmberIcon, { "aria-hidden": true }) : _jsx(InfoOutlinedIcon, { "aria-hidden": true }), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsx(NoticeTitle, { children: isPending
                                    ? t("approvalsScreen.review.whyTitle")
                                    : t("approvalsScreen.review.decisionTitle") }), _jsx(Box, { sx: { maxWidth: "720px" }, children: why() }), decision && (_jsxs(TagRow, { sx: { marginTop: "12px" }, children: [_jsx(Overline, { sx: { margin: "0 4px 0 0" }, children: decision.rule === null
                                            ? t("approvalsScreen.review.ruleLast", {
                                                version: decision.matrixVersion,
                                            })
                                            : t("approvalsScreen.review.rule", {
                                                rule: decision.rule,
                                                version: decision.matrixVersion,
                                            }) }), decision.rule !== null &&
                                        conditionLabels(decision.conditions, t, fieldLabel).map(function (label) { return (_jsx(Tag, { children: label }, label)); }), onViewRule && (_jsx(Button, { variant: "secondary", size: "small", sx: { marginLeft: "auto", minHeight: "36px" }, onClick: function () {
                                            return onViewRule({
                                                version: decision.matrixVersion,
                                                rule: decision.rule,
                                            });
                                        }, children: t("approvalsScreen.review.seeRule") }))] }))] })] }), canDecide ? (_jsxs(_Fragment, { children: [_jsx(Stepper, { "aria-label": String(t("approvalsScreen.flow.stepsLabel")), children: STEPS.map(function (key, index) { return (_jsxs(StepItem, { "aria-current": step === key ? "step" : undefined, "data-done": done(key), children: [_jsx(NumberBadge, { "data-pending": step !== key && !done(key), sx: { width: "24px", height: "24px", fontSize: "12px" }, children: index + 1 }), t("approvalsScreen.flow.steps.".concat(key))] }, key)); }) }), section("identity", identitySummary, identityStep), section("voter", voterSummary, _jsx(ApprovalVoterStep, { candidates: candidates, comparedFields: comparedFields, fieldLabel: fieldLabel, chosen: chosen, onChoose: function (value) {
                            setChosen(value);
                            setVerdict(null);
                        }, search: search, onSearch: setSearch, loading: lookup.isLoading, failed: lookup.isError, jointNames: jointNames })), section("decide", "", decideStep)] })) : (_jsx(Box, { sx: { marginTop: "24px" }, children: identityStep })), _jsxs(Footer, { children: [backButton, canDecide &&
                        (step !== "decide" ? (_jsx(Button, { onClick: next, endIcon: _jsx(ArrowForwardIosIcon, {}), sx: { minWidth: "220px" }, children: t("approvalsScreen.flow.continue") })) : effectiveVerdict === "reject" ? (_jsx(Button, { disabled: sending || messageMissing, onClick: reject, sx: {
                                "minWidth": "220px",
                                "backgroundColor": "#DC2626",
                                "borderColor": "#DC2626",
                                "&:hover, &:focus": { backgroundColor: "#B91C1C" },
                            }, children: t("approvalsScreen.review.reject") })) : (_jsx(Button, { disabled: sending || !canApprove || effectiveVerdict !== "approve", onClick: function () { return setConfirmApprove(true); }, sx: { minWidth: "220px" }, children: t("approvalsScreen.review.approve") })))] }), _jsxs(Dialog, { variant: "info", open: confirmApprove, title: String(t("approvalsScreen.review.approveDialog.title", { name: name })), ok: String(t("approvalsScreen.review.approveDialog.confirm")), cancel: String(t("common.label.cancel")), handleClose: function (result) {
                    if (result) {
                        approve();
                    }
                    else {
                        setConfirmApprove(false);
                    }
                }, children: [_jsx("p", { children: t("approvalsScreen.review.approveDialog.body") }), chosenCandidate && (_jsxs(Notice, { "data-tone": "neutral", sx: { alignItems: "center" }, children: [_jsx(Avatar, { "aria-hidden": true, children: initials(voterName(chosenCandidate.voter)) }), _jsxs("div", { children: [_jsx(OptionTitle, { children: voterName(chosenCandidate.voter) }), _jsx(Muted, { children: t("approvalsScreen.review.detailsMatch", {
                                            count: chosenCandidate.matching,
                                            total: chosenCandidate.details.length,
                                        }) })] })] })), typedByHand && faceToFace && (_jsx("p", { children: t("approvalsScreen.review.approveDialog.checked") })), _jsx(Box, { component: "p", sx: { marginBottom: 0 }, children: _jsx(Muted, { children: t("approvalsScreen.review.approveDialog.irreversible") }) })] })] }));
};
